import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { trackEvent, setAnalyticsUser, setAnalyticsUserProperties } from '../config/firebase';

// Create the analytics context
export const AnalyticsContext = createContext();

// Custom hook to use the analytics context
export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};

export const AnalyticsProvider = ({ children }) => {
  const { user, loading } = useAuth();
  const [hasConsent, setHasConsent] = useState(false);
  
  // Check for analytics consent on mount and when it changes
  useEffect(() => {
    const checkConsent = () => {
      const savedConsent = localStorage.getItem('cookie-consent');
      if (savedConsent) {
        try {
          const parsedConsent = JSON.parse(savedConsent);
          setHasConsent(parsedConsent.analytics === true);
          
          // Update Google Analytics consent state when it changes
          if (window.gtag) {
            window.gtag('consent', 'update', {
              'analytics_storage': parsedConsent.analytics ? 'granted' : 'denied'
            });
          }
        } catch (e) {
          console.error('Error parsing consent data:', e);
          setHasConsent(false);
        }
      } else {
        setHasConsent(false);
      }
    };
    
    // Check consent immediately
    checkConsent();
    
    // Add storage event listener to detect consent changes
    const handleStorageChange = (e) => {
      if (e.key === 'cookie-consent') {
        checkConsent();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Set the user ID for analytics when the user logs in
  useEffect(() => {
    if (user && !loading && hasConsent) {
      // Set the user ID for Firebase Analytics
      setAnalyticsUser(user.uid);
      
      // Track basic user properties for segmentation
      setAnalyticsUserProperties({
        subscription_status: user.subscriptionTier || 'free',
        is_pro_user: !!user.subscriptionTier && user.subscriptionTier !== 'free'
      });

      // Track login event
      trackEvent('user_login');
    }
  }, [user, loading, hasConsent]);

  // Define simple tracking functions focused on basic metrics that respect user consent
  const trackPageView = (pageName) => {
    if (hasConsent) {
      trackEvent('page_view', { page_name: pageName });
    }
  };

  const trackSignUp = () => {
    if (hasConsent) {
      trackEvent('sign_up');
    }
  };

  const trackSubscriptionChange = (newStatus) => {
    if (hasConsent) {
      trackEvent('subscription_change', { new_status: newStatus });
    }
  };
  
  // Wrapper for generic event tracking that respects consent
  const safeTrackEvent = (eventName, eventParams) => {
    if (hasConsent) {
      trackEvent(eventName, eventParams);
    }
  };

  // Return the provider with simplified tracking functions
  return (
    <AnalyticsContext.Provider value={{
      trackPageView,
      trackSignUp,
      trackSubscriptionChange,
      trackEvent: safeTrackEvent,
      hasAnalyticsConsent: hasConsent
    }}>
      {children}
    </AnalyticsContext.Provider>
  );
};
